import { SDK_LIMITS, SDK_ERRORS } from '@riz/contracts/sdk';
import { rejectWith } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface ScoreSubmission {
  accepted: boolean;
  score: number;
  /** The grade Riz recorded this submission at; see the score-trust plan. */
  trustLevel?: string;
  [key: string]: unknown;
}

export interface ScoreModule {
  submit(score: number, details?: Record<string, unknown>): Promise<ScoreSubmission>;
  best(): number;
}

export function createScoreModule(client: SdkClient): ScoreModule {
  return {
    async submit(score, details) {
      const capability = client.requireCapability<ScoreSubmission>('score.submit', 'Score submission');
      if (capability) return capability;

      const value = clampSubmittedScore(score);
      if (value === null) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'score.submit(score) needs a finite number.');
      }
      const best = client.raiseBestScore(value);
      const result = await client.track('score_submitted', { ...details, score: value });
      return {
        accepted: result.accepted !== false,
        score: typeof result.score === 'number' ? result.score : best,
        trustLevel: typeof result.trustLevel === 'string' ? result.trustLevel : undefined,
        ...result,
      };
    },

    best() {
      return client.bestScoreSoFar;
    },
  };
}

/**
 * Clamped rather than rejected at the ceiling: a game that overshoots should
 * still record its run. The server applies the real per-game ceiling, so this
 * only spares an obviously bad value a round trip.
 */
export function clampSubmittedScore(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(SDK_LIMITS.maxScore, Math.floor(numeric)));
}
